import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconCoin, IconEye, IconKey, IconLockOff } from "@tabler/icons-react";
import { UserSorting, useSearchUsers } from "./api/useSearchUsers";
import { useTriggerPasswordChange } from "./api/useTriggerPasswordChange";
import { useSetUserCreditLimit } from "./api/useSetUserCreditLimit";
import { dateTimeToString } from "../../shared/utils/Dates";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import CreateApiTokenModal from "@/features/admin-users-list/components/CreateApiTokenModal";

export default function AdminUsersList() {
  const { users, searchUsers } = useSearchUsers();
  const { setCreditLimit } = useSetUserCreditLimit();
  const navigate = useNavigate();
  const { triggerPasswordChange } = useTriggerPasswordChange();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<{ field: UserSorting; asc: boolean }>({
    field: UserSorting.Email,
    asc: true,
  });
  const [filters, setFilters] = useState<{
    email: string;
    userId: string;
  }>({
    email: "",
    userId: "",
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const [isTokenDialogOpened, setIsTokenDialogOpened] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserEmail, setSelectedUserEmail] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    searchUsers({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sorting: sorting.field,
      sortingAsc: sorting.asc,
      email: debouncedFilters.email,
      userId: debouncedFilters.userId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, sorting, debouncedFilters]);

  const [creditLimitDialog, setCreditLimitDialog] = useState<{
    open: boolean;
    userId: string;
    email: string;
    currentLimit: number;
  }>({
    open: false,
    userId: "",
    email: "",
    currentLimit: 0,
  });
  const [newCreditLimit, setNewCreditLimit] = useState("");

  const handleSort = (field: UserSorting) => {
    setSorting((prev) => ({
      field,
      asc: prev.field === field ? !prev.asc : true,
    }));
  };

  const handleViewChatsClick = (userId: string) => {
    navigate(`/admin/users/${userId}/chats`);
  };

  const handleResetPasswordClick = (userId: string, email: string) => {
    if (window.confirm(`Reset password for ${email}?`)) {
      triggerPasswordChange(userId);
      alert(`Password reset email sent to ${email}`);
    }
  };

  const handleSetCreditLimit = () => {
    if (newCreditLimit && creditLimitDialog.userId) {
      setCreditLimit(creditLimitDialog.userId, parseInt(newCreditLimit)).then(
        () => {
          // Refresh the users list after setting the credit limit
          searchUsers({
            page: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            sorting: sorting.field,
            sortingAsc: sorting.asc,
            email: debouncedFilters.email,
            userId: debouncedFilters.userId,
          });
        },
      );
      setCreditLimitDialog((prev) => ({ ...prev, open: false }));
      setNewCreditLimit("");
    }
  };

  const handleCreateTokenClick = (userId: string, email: string) => {
    setSelectedUserId(userId);
    setSelectedUserEmail(email);
    setIsTokenDialogOpened(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Input
          placeholder="Filter by Email"
          value={filters.email}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, email: e.target.value }))
          }
          className="max-w-xs"
        />
        <Input
          placeholder="Filter by User ID"
          value={filters.userId}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, userId: e.target.value }))
          }
          className="max-w-xs"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-center">UserId</TableHead>
              <TableHead
                className="cursor-pointer text-center"
                onClick={() => handleSort(UserSorting.Email)}
              >
                Email{" "}
                {sorting.field === UserSorting.Email &&
                  (sorting.asc ? "↑" : "↓")}
              </TableHead>
              <TableHead
                className="cursor-pointer text-center"
                onClick={() => handleSort(UserSorting.CreatedAt)}
              >
                Created At{" "}
                {sorting.field === UserSorting.CreatedAt &&
                  (sorting.asc ? "↑" : "↓")}
              </TableHead>
              <TableHead
                className="cursor-pointer text-center"
                onClick={() => handleSort(UserSorting.CreditLimit)}
              >
                Credit Limit{" "}
                {sorting.field === UserSorting.CreditLimit &&
                  (sorting.asc ? "↑" : "↓")}
              </TableHead>
              <TableHead
                className="cursor-pointer text-center"
                onClick={() => handleSort(UserSorting.CreditsUsed)}
              >
                Credits Used{" "}
                {sorting.field === UserSorting.CreditsUsed &&
                  (sorting.asc ? "↑" : "↓")}
              </TableHead>
              <TableHead className="text-center">View chats</TableHead>
              <TableHead className="text-center">Reset password</TableHead>
              <TableHead className="text-center">Set credit limit</TableHead>
              <TableHead className="text-center">API token</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users?.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="text-center">{user.id}</TableCell>
                <TableCell className="text-center">{user.email}</TableCell>
                <TableCell className="text-center">
                  {dateTimeToString(user.createdAt)}
                </TableCell>
                <TableCell className="text-center">
                  {user.creditLimit}
                </TableCell>
                <TableCell className="text-center">
                  {user.creditsUsed}
                </TableCell>
                <TableCell className="text-center">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => handleViewChatsClick(user.id)}
                  >
                    <IconEye className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell className="text-center">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() =>
                      handleResetPasswordClick(user.id, user.email)
                    }
                  >
                    <IconLockOff className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell className="text-center">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() =>
                      setCreditLimitDialog({
                        open: true,
                        userId: user.id,
                        email: user.email,
                        currentLimit: user.creditLimit,
                      })
                    }
                  >
                    <IconCoin className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell className="text-center">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      handleCreateTokenClick(user.id, user.email);
                    }}
                  >
                    <IconKey className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() =>
              setPagination((prev) => ({
                ...prev,
                pageIndex: Math.max(0, prev.pageIndex - 1),
              }))
            }
            disabled={pagination.pageIndex === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              setPagination((prev) => ({
                ...prev,
                pageIndex: prev.pageIndex + 1,
              }))
            }
          >
            Next
          </Button>
        </div>
        <div>Page {pagination.pageIndex + 1}</div>
      </div>

      <Dialog
        open={creditLimitDialog.open}
        onOpenChange={(open) =>
          setCreditLimitDialog((prev) => ({ ...prev, open }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Credit Limit</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-4">
              Enter new credit limit for {creditLimitDialog.email}
            </p>
            <Input
              type="number"
              value={newCreditLimit}
              onChange={(e) => setNewCreditLimit(e.target.value)}
              placeholder={creditLimitDialog.currentLimit.toString()}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setCreditLimitDialog((prev) => ({ ...prev, open: false }))
              }
            >
              Cancel
            </Button>
            <Button onClick={handleSetCreditLimit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CreateApiTokenModal
        opened={isTokenDialogOpened}
        close={() => setIsTokenDialogOpened(false)}
        userId={selectedUserId}
        email={selectedUserEmail}
      />
    </div>
  );
}
